import { Box } from "@material-ui/core";
import { DeleteOutline, Edit } from "@material-ui/icons";
import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { deleteAssetClass } from "../../../services/AssetClasses/assetClassApisService";
import { getAssetClasses } from "../../../services/AssetClasses/assetClassApisService.js";
import { DisplayMessage } from "../../../services/helpers";
import { useStyles } from "../../../styles/styles";
import AddItemButton from "../../Common/Buttons/AddItemButton.js";
import Header from "../../Common/Headers/Header.js";
import DeleteConfirmationModal from "../../Common/Modals/DeleteConfirmationModal.js";
import TableComponent from "../../Common/Table/TableComponent.js";
import { sortTableBooleans, sortTableStringsNotCaseSensitive } from "../../../utils/tableSorting.js";
import AssetGroupsDropdown from "../../Common/Dropdowns/AssetGroupsDropdown.js";

const AssetClassesTable = ({ resources, refreshGrid }) => {
  const classes = useStyles();
  const [allAssetClasses, setAllAssetClasses] = useState([]);
  const [assetClasses, setAssetClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedAssetClassId, setSelectedAssetClassId] = useState(null);
  const [selectedAssetGroupId, setSelectedAssetGroupId] = useState(0);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const history = useHistory();

  const tableSettings = {
    actions: [
      {
        icon: () => <Edit />,
        tooltip: resources["Edit"],
        onClick: (event, rowData) => {
          history.push({
            pathname: `/assetclasses/${rowData.id}`,
            state: { assetClassId: rowData.id },
          });
        },
      },
      {
        icon: () => <DeleteOutline />,
        tooltip: resources["Delete"],
        onClick: (event, rowData) => handleDelete(rowData.id),
      },
    ],
    columns: [
      {
        title: resources["Code"],
        field: "code",
        cellStyle: { width: "20%", minWidth: "150px" },
        headerStyle: { width: "20%", minWidth: "150px" },
        customSort: (a, b) => sortTableStringsNotCaseSensitive(a.code, b.code)
      },
      {
        title: resources["Description"],
        field: "description",
        cellStyle: { width: "35%" },
        headerStyle: { width: "40%" },
        customSort: (a, b) => sortTableStringsNotCaseSensitive(a.description, b.description)
      },
      {
        title: resources["Group"],
        field: "assetGroupName",
        cellStyle: { width: "35%" },
        headerStyle: { width: "40%" },
        customSort: (a, b) => sortTableStringsNotCaseSensitive(a.assetGroupName, b.assetGroupName)
      },
      {
        title: resources["IsActive"],
        field: "isActive",
        cellStyle: { width: "5%", minWidth: "150px" },
        headerStyle: { width: "5%" },
        customSort: (a, b) => sortTableBooleans(a.isActive, b.isActive)
      },
      {
        title: resources["Currency"],
        field: "currency",
        cellStyle: { width: "5%", minWidth: "150px" },
        headerStyle: { width: "5%" },
        customSort: (a, b) => sortTableStringsNotCaseSensitive(a.currency, b.currency)
      },
    ],
  };

  const handleDelete = (id) => {
    setSelectedAssetClassId(id);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedAssetClassId(null);
    setDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    if (selectedAssetClassId) {
      const result = await deleteAssetClass(selectedAssetClassId);

      if (result) {
        setAssetClasses((prev) =>
          prev.filter((ac) => ac.id !== selectedAssetClassId)
        );
        DisplayMessage(resources["AssetClassWasDeletedWithSuccess"], "success");
      }
    }
    closeDeleteModal();
  };

  const filterAssetClasses = useCallback(
    (groupId) => {
      if (groupId > 0) {
        setAssetClasses(
          allAssetClasses.filter((ac) => ac.assetGroupId === groupId)
        );
      } else {
        setAssetClasses(allAssetClasses);
      }
    },
    [allAssetClasses]
  );

  const fetchAssetClasses = useCallback(async () => {
    setLoading(true);
    const data = await getAssetClasses();
    if (data) {
      const formattedData = data.sort((el1, el2) => el1.id - el2.id).map(
        (item) => ({
          id: item.id,
          code: item.code,
          description: item.description,
          isActive: item.isActive,
          assetGroupId: item.assetGroup?.id,
          assetGroupName: item.assetGroup?.description,
          currency: item.currency,
        })
      );
      setAllAssetClasses(formattedData);
      setAssetClasses(formattedData);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    filterAssetClasses(selectedAssetGroupId);
  }, [filterAssetClasses, selectedAssetGroupId]);

  useEffect(() => {
    if (isFirstTime || refreshGrid) {
      fetchAssetClasses();
      setIsFirstTime(false);
    }
  }, [fetchAssetClasses, isFirstTime, refreshGrid]);

  return (
    <Box>
      <Box className="position-relative py-4">
        <Header title={resources["AssetClasses"]} loading={loading} />
        {!loading && (
          <>
            <Box className="d-flex form-group">
              <AssetGroupsDropdown
                id="assetgroups-dropdown-grid"
                containerClass="col-5"
                resources={resources}
                assetGroup={selectedAssetGroupId}
                onAssetGroupChange={(e) => setSelectedAssetGroupId(parseInt(e.target.value))}
              />
              <AddItemButton
                title={resources["AddAssetClass"]}
                linkPath={"/assetclasses/new"}
                state={{ assetGroupId: selectedAssetGroupId }}
                idName="class"
              />
            </Box>
            <TableComponent
              resources={resources}
              data={assetClasses}
              tableSettings={tableSettings}
            />
          </>
        )}
        <DeleteConfirmationModal
          onConfirm={confirmDelete}
          resources={resources}
          areYouSureMessage={resources["AreYouSureYouWantToDeleteAssetClass"]}
          open={deleteModalOpen}
          onClose={closeDeleteModal}
          className={classes.modal}
        />
      </Box>
    </Box>
  );
};

export default AssetClassesTable;
